/**
 * Hand-typed shapes mirroring the JsonResource outputs from the Laravel API.
 *
 * Scribe doesn't (yet) introspect JsonResource::toArray() return shapes,
 * so the auto-generated client returns `Promise<void>`. We keep these
 * shapes here as a single source of truth so pages can typecast safely.
 *
 * KEEP IN SYNC with base/app/Http/Resources/*.php — same field names.
 */

export interface User {
  id: number;
  name: string;
  username: string | null;
  /** Only returned for your own profile — absent when viewing someone else. */
  email?: string;
  bio: string | null;
  avatar_url: string | null;
  city: string | null;
  country: string | null;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

export interface CarVideo {
  id: number;
  car_id: number;
  url: string;
  title: string | null;
  position: number;
  created_at: string;
  updated_at: string;
}

export type ContributionStatus = 'pending' | 'approved' | 'denied';

export interface Car {
  id: number;
  user_id: number;
  garage_id: number | null;
  /** Optional — not everyone names their car. Show `display_name` instead. */
  nickname: string | null;
  /** The nickname, or "Make Model" for a car without one. */
  display_name: string;
  slug: string;
  intro: string | null;
  summary: string | null;
  make: string;
  model: string;
  year: number;
  photo_urls: string[];
  is_public: boolean;
  rating_average: number;
  rating_count: number;
  my_rating: number | null;
  my_contribution_status: ContributionStatus | null;
  can_contribute: boolean;
  user?: User;
  videos?: CarVideo[];
  activities?: Activity[];
  /** Only loaded where the viewer may read them (the garage page). */
  goals?: Goal[];
  created_at: string;
  updated_at: string;
}

export interface Garage {
  id: number;
  user_id: number;
  name: string;
  slug: string;
  car_count?: number;
  follower_count?: number;
  /** How often the current user has used this garage — most-used is listed first. */
  my_visit_count?: number;
  user?: User;
  /** Only present on GET /garages/{id} — each car carries its goals. */
  cars?: Car[];
  created_at: string;
  updated_at: string;
}

export interface GarageFollower {
  id: number;
  garage_id: number;
  user_id: number;
  status: ContributionStatus;
  responded_at: string | null;
  user?: User;
  garage?: Garage;
  created_at: string;
  updated_at: string;
}

export interface CarContributor {
  id: number;
  car_id: number;
  user_id: number;
  status: ContributionStatus;
  responded_at: string | null;
  user?: User;
  car?: Car;
  created_at: string;
  updated_at: string;
}

export interface Activity {
  id: number;
  user_id: number;
  car_id: number | null;
  type: string;
  type_label: string;
  summary: string;
  location_name: string | null;
  lat: string | null;
  lng: string | null;
  scheduled_at: string | null;
  is_public: boolean;
  user?: User;
  car?: Car;
  created_at: string;
  updated_at: string;
}

export const GOAL_PRIORITIES = ['high', 'medium', 'low'] as const;
export type GoalPriority = (typeof GOAL_PRIORITIES)[number];

export const GOAL_STATUSES = ['active', 'done', 'dropped'] as const;
export type GoalStatus = (typeof GOAL_STATUSES)[number];

export const GOAL_TASK_TYPES = ['buy', 'do'] as const;
export type GoalTaskType = (typeof GOAL_TASK_TYPES)[number];

export type GoalTaskStatus = 'open' | 'completed';

export const GOAL_TASK_DURATION_UNITS = ['day', 'week', 'month'] as const;
export type GoalTaskDurationUnit = (typeof GOAL_TASK_DURATION_UNITS)[number];

export interface GoalTask {
  id: number;
  goal_id: number;
  type: GoalTaskType;
  status: GoalTaskStatus;
  title: string;
  note: string | null;
  price_cents: number | null;
  /** A job's estimated duration — always null for a part to buy. */
  duration_value: number | null;
  duration_unit: GoalTaskDurationUnit | null;
  created_at: string;
  updated_at: string;
}

/** A known part or job name offered while typing a goal task (GET /task-terms). */
export interface TaskTerm {
  id: number;
  key: string;
  type: GoalTaskType;
  /** In the app's current language. */
  name: string;
  category: string;
}

export interface GoalPhoto {
  id: number;
  url: string;
}

export interface Goal {
  id: number;
  car_id: number;
  title: string;
  description: string | null;
  priority: GoalPriority;
  status: GoalStatus;
  /** Optional, as YYYY-MM-DD. */
  deadline: string | null;
  tasks: GoalTask[];
  /** Always the first entry of `photos`; null when the goal has none. */
  cover_photo_id: number | null;
  photos: GoalPhoto[];
  created_at: string;
  updated_at: string;
}

export interface Event {
  id: number;
  organizer_id: number;
  title: string;
  slug: string;
  description: string | null;
  type: string;
  type_label: string;
  starts_at: string;
  ends_at: string | null;
  location_name: string;
  city: string | null;
  country: string | null;
  lat: string | null;
  lng: string | null;
  max_participants: number | null;
  photo_urls: string[];
  participant_count?: number;
  organizer?: User;
  created_at: string;
  updated_at: string;
}

export interface BazaarItem {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  external_url: string;
  photo_urls: string[];
  price_cents: number | null;
  currency: string | null;
  created_at: string;
  updated_at: string;
}

export interface ForumCategory {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  position: number;
  thread_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ForumThread {
  id: number;
  forum_category_id: number;
  car_id: number | null;
  user_id: number;
  title: string;
  slug: string;
  body: string;
  is_pinned: boolean;
  is_locked: boolean;
  last_post_at: string | null;
  post_count?: number;
  category?: ForumCategory;
  car?: Car;
  user?: User;
  posts?: ForumPost[];
  created_at: string;
  updated_at: string;
}

export interface ForumPost {
  id: number;
  forum_thread_id: number;
  user_id: number;
  body: string;
  user?: User;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: number;
  conversation_id: number;
  user_id: number;
  body: string;
  user?: User;
  created_at: string;
  updated_at: string;
}

export interface Conversation {
  id: number;
  car_id: number;
  last_message_at: string | null;
  unread_count: number;
  other_participant?: User;
  car?: Car;
  latest_message?: Message;
  messages?: Message[];
  created_at: string;
  updated_at: string;
}

export interface AppNotificationData {
  type: string;
  conversation_id?: number;
  car_id?: number;
  car_nickname?: string | null;
  sender_id?: number;
  sender_name?: string | null;
  preview?: string;
  message_id?: number;
  [key: string]: unknown;
}

export interface AppNotification {
  id: string;
  type: string;
  data: AppNotificationData;
  read_at: string | null;
  created_at: string;
}

export interface PaginatedMeta {
  current_page: number;
  from: number | null;
  last_page: number;
  per_page: number;
  to: number | null;
  total: number;
}

export interface Paginated<T> {
  data: T[];
  meta: PaginatedMeta;
  links: { first: string; last: string; prev: string | null; next: string | null };
}

export interface SingleResponse<T> {
  data: T;
}

export interface AuthResponse {
  data: {
    user: User;
    token: string;
    token_type: string;
  };
  message: string;
}

export interface SiteStats {
  cars: number;
  events: number;
  members: number;
}

export type RobotsDirective =
  | 'index, follow'
  | 'index, nofollow'
  | 'noindex, follow'
  | 'noindex, nofollow'
  | string
  | null;

export interface PageSeo {
  title: string | null;
  description: string | null;
  author: string | null;
  robots: RobotsDirective;
  canonical_url: string | null;
  image: string | null;
}

export type PageFlagName = 'footer' | 'header' | string;

export interface PageFlag {
  flag: PageFlagName;
  sort_order: number;
}

export interface Page {
  id: number;
  slug: string;
  title: string;
  layout: string;
  html: string;
  is_published: boolean;
  published_at: string | null;
  flags: PageFlag[];
  seo: PageSeo;
  updated_at: string;
}

export interface FlaggedPage {
  slug: string;
  title: string;
  sort_order: number;
}
